import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    Box, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody,
    TablePagination, TextField, IconButton, Grid, Typography, CircularProgress,
    Snackbar, Alert, Stack, MenuItem, Select, FormControl, InputLabel, Button,
    Chip, Tooltip, Card, CardContent, Dialog, DialogTitle, DialogContent, DialogActions,
    Divider, Avatar
} from "@mui/material";
import {
    Visibility as VisibilityIcon,
    CheckCircle as CheckCircleIcon,
    Payment as PaymentIcon,
    Lock as LockIcon,
    Refresh as RefreshIcon,
    RestartAlt as ResetIcon,
    SearchOff as NoDataIcon,
    Payments as PaymentsIcon,
    AccountBalanceWallet as AccountBalanceWalletIcon,
    Summarize as SummarizeIcon,
    Close as CloseIcon,
    People as PeopleIcon,
    CheckCircleOutline as ApprovedCountIcon,
    HelpOutline as HelpIcon
} from "@mui/icons-material";
import { getData, postData, putData } from "../../../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../../../services/endpoints";
import StatsCard from "../../../../../components/common/StatsCard";

function ReviewStepper({ batchData = [], payPeriod }) {
    const [batchList, setBatchList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    // Filter & Search States
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");

    // Pagination States
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Breakdown Dialog States
    const [batchDetailOpen, setBatchDetailOpen] = useState(false);
    const [selectedBatchDetail, setSelectedBatchDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // Action Dialog States
    const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false);
    const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);

    // Feedback Snackbar
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const getShortMonth = (fullMonth) => {
        if (!fullMonth) return "";
        const monthPart = fullMonth.split(" ")[0];
        return new Date(`${monthPart} 1, 2020`).toLocaleString("en-US", { month: "short" });
    };

    const parsedPeriod = useMemo(() => {
        if (!payPeriod) return { year: "", shortMonth: "" };
        const parts = payPeriod.split(" ");
        const year = parts[1] || parts[0] || "";
        const monthName = parts[0] || "";
        return { year, shortMonth: getShortMonth(monthName) };
    }, [payPeriod]);

    const fetchBatchData = useCallback(async () => {
        if (!payPeriod || !parsedPeriod.year || !parsedPeriod.shortMonth) {
            setBatchList(batchData || []);
            return;
        }

        setLoading(true);
        try {
            const res = await getData(API_ENDPOINTS.getBatchOverview(parsedPeriod.year, parsedPeriod.shortMonth));
            if (res?.statusCode === 200 && Array.isArray(res.result)) {
                setBatchList(res.result);
            } else {
                setBatchList(batchData || []);
            }
        } catch (error) {
            setBatchList(batchData || []);
        } finally {
            setLoading(false);
        }
    }, [payPeriod, parsedPeriod, batchData]);

    useEffect(() => {
        fetchBatchData();
    }, [fetchBatchData]);

    // --- Summary Metrics ---
    const summaryMetrics = useMemo(() => {
        const totalEmployees = batchList.length;
        let totalGross = 0;
        let totalDeductions = 0;
        let totalReimbursements = 0;
        let netPayable = 0;
        let lockedCount = 0;
        let finalizedCount = 0;
        let paidCount = 0;

        batchList.forEach((emp) => {
            const gross = parseFloat(emp.totalGross || 0);
            const deductions = parseFloat(emp.totalDeductions || 0);
            const reimbursements = parseFloat(emp.reimbursementAmount || 0);
            const net = parseFloat(emp.netSalary || (gross - deductions + reimbursements));

            totalGross += gross;
            totalDeductions += deductions;
            totalReimbursements += reimbursements;
            netPayable += net;

            if (emp.locked) lockedCount += 1;
            if (emp.finalized) finalizedCount += 1;
            if (emp.payed_flag) paidCount += 1;
        });

        const isFullyLocked = totalEmployees > 0 && lockedCount === totalEmployees;
        const isFullyFinalized = totalEmployees > 0 && finalizedCount === totalEmployees;
        const isFullyPaid = totalEmployees > 0 && paidCount === totalEmployees;

        return {
            totalEmployees,
            totalGross,
            totalDeductions,
            totalReimbursements,
            netPayable,
            lockedCount,
            finalizedCount,
            paidCount,
            isFullyLocked,
            isFullyFinalized,
            isFullyPaid,
        };
    }, [batchList]);

    // --- Search & Filter Logic ---
    const filteredEmployees = useMemo(() => {
        return batchList.filter((emp) => {
            const matchesSearch =
                !searchQuery ||
                emp.empId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                emp.name?.toLowerCase().includes(searchQuery.toLowerCase());

            let matchesStatus = true;
            if (statusFilter === "Paid") matchesStatus = emp.payed_flag === true;
            else if (statusFilter === "Finalized") matchesStatus = emp.finalized === true && !emp.payed_flag;
            else if (statusFilter === "Locked") matchesStatus = emp.locked === true && !emp.finalized;
            else if (statusFilter === "Pending") matchesStatus = !emp.locked;

            return matchesSearch && matchesStatus;
        });
    }, [batchList, searchQuery, statusFilter]);

    // --- Action: View Employee Breakdown ---
    const handleViewBreakdown = async (row) => {
        setBatchDetailOpen(true);
        setDetailLoading(true);

        try {
            if (row.id) {
                const res = await getData(API_ENDPOINTS.getBatchPayrollById(row.id));
                if (res?.statusCode === 200 && res.result) {
                    setSelectedBatchDetail(res.result);
                } else {
                    setSelectedBatchDetail(row);
                }
            } else {
                setSelectedBatchDetail(row);
            }
        } catch (err) {
            setSelectedBatchDetail(row);
        } finally {
            setDetailLoading(false);
        }
    };

    // --- Action: Finalize & Lock Payroll ---
    const handleFinalizeBatch = async () => {
        if (!parsedPeriod.year || !parsedPeriod.shortMonth) {
            setSnackbar({ open: true, message: "Invalid pay period selection", severity: "error" });
            return;
        }

        setActionLoading(true);
        try {
            // Lock payroll first if needed
            if (!summaryMetrics.isFullyLocked) {
                const lockPayload = { month: parsedPeriod.shortMonth, year: Number(parsedPeriod.year) };
                await putData(API_ENDPOINTS.updateBatchPayrollLock, lockPayload);
            }

            // Finalize payroll
            const finalizePayload = {
                month: parsedPeriod.shortMonth,
                year: Number(parsedPeriod.year),
                finalized: true,
            };
            const res = await putData(API_ENDPOINTS.updateBatchPayrollLockAndFinalize, finalizePayload);

            if (res?.statusCode === 200) {
                setSnackbar({ open: true, message: `Payroll for ${payPeriod} has been locked and finalized successfully!`, severity: "success" });
                setFinalizeDialogOpen(false);
                fetchBatchData();
            } else {
                setSnackbar({ open: true, message: res?.message || "Failed to finalize payroll", severity: "error" });
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Error locking and finalizing payroll", severity: "error" });
        } finally {
            setActionLoading(false);
        }
    };

    // --- Action: Disburse Payouts ---
    const handleDisbursePayout = async () => {
        if (!parsedPeriod.year || !parsedPeriod.shortMonth) {
            setSnackbar({ open: true, message: "Invalid pay period selection", severity: "error" });
            return;
        }

        setActionLoading(true);
        try {
            const payload = { month: parsedPeriod.shortMonth, year: parsedPeriod.year };
            const res = await postData(API_ENDPOINTS.disbursePayrollPayouts, payload);

            if (res?.statusCode === 200) {
                setSnackbar({ open: true, message: res.message || `Payment completed for ${payPeriod}.`, severity: "success" });
                setPayoutDialogOpen(false);
                fetchBatchData();
            } else {
                setSnackbar({ open: true, message: res?.message || "Failed to disburse payments", severity: "error" });
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Error processing payment disbursement", severity: "error" });
        } finally {
            setActionLoading(false);
        }
    };

    const handleReset = () => {
        setSearchQuery("");
        setStatusFilter("All");
        setPage(0);
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const DynamicRow = ({ label, value, isBold = false, color = "text.secondary" }) => {
        const numericValue = parseFloat(value || 0);
        if (numericValue <= 0) return null;

        return (
            <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell sx={{ py: 1, border: 0, color }}>{label}</TableCell>
                <TableCell align="right" sx={{ py: 1, border: 0, fontWeight: isBold ? 700 : 500 }}>
                    ₹{numericValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </TableCell>
            </TableRow>
        );
    };

    if (loading && batchList.length === 0) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;
    }

    return (
        <Box>
            {/* Header & Batch Controls */}
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} mb={3}>
                <Box>
                    <Typography variant="h6" fontWeight="bold">Payroll Final Review</Typography>
                    <Typography variant="body2" color="textSecondary">
                        Review summary metrics, verify employee earnings & deductions, and lock/finalize batch for {payPeriod}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1.5}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={fetchBatchData}
                        sx={{ borderRadius: 2, textTransform: 'none' }}
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        startIcon={<LockIcon />}
                        disabled={summaryMetrics.isFullyFinalized}
                        onClick={() => setFinalizeDialogOpen(true)}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                    >
                        {summaryMetrics.isFullyFinalized ? "Batch Finalized" : "Finalize & Lock Batch"}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<PaymentIcon />}
                        disabled={!summaryMetrics.isFullyFinalized || summaryMetrics.isFullyPaid}
                        onClick={() => setPayoutDialogOpen(true)}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                    >
                        {summaryMetrics.isFullyPaid ? "Paid Out" : "Disburse Payouts"}
                    </Button>
                </Stack>
            </Stack>

            {/* Summary KPI Cards */}
            <Grid container spacing={2} mb={3}>
                <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatsCard
                        title="Total Scope Employees"
                        value={summaryMetrics.totalEmployees}
                        icon={<PeopleIcon />}
                        color="#6366F1"
                    />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatsCard
                        title="Total Gross Payroll"
                        value={`₹${summaryMetrics.totalGross.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                        icon={<PaymentsIcon />}
                        color="#10B981"
                    />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatsCard
                        title="Total Deductions"
                        value={`₹${summaryMetrics.totalDeductions.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                        icon={<AccountBalanceWalletIcon />}
                        color="#EF4444"
                    />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatsCard
                        title="Net Take-Home Salary"
                        value={`₹${summaryMetrics.netPayable.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                        icon={<ApprovedCountIcon />}
                        color="#3B82F6"
                    />
                </Grid>
            </Grid>

            {/* Batch Status Summary Banner */}
            <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 3, bgcolor: '#F8FAFC', borderColor: '#E2E8F0' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item size={{ xs: 12, sm: 4 }}>
                        <Typography variant="caption" color="textSecondary" textTransform="uppercase" fontWeight={700}>Batch Lock Status</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Chip
                                label={summaryMetrics.isFullyLocked ? "Locked" : `${summaryMetrics.lockedCount}/${summaryMetrics.totalEmployees} Locked`}
                                color={summaryMetrics.isFullyLocked ? "success" : "warning"}
                                size="small"
                                sx={{ fontWeight: 700 }}
                            />
                        </Box>
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 4 }}>
                        <Typography variant="caption" color="textSecondary" textTransform="uppercase" fontWeight={700}>Finalize Status</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Chip
                                label={summaryMetrics.isFullyFinalized ? "Finalized" : `${summaryMetrics.finalizedCount}/${summaryMetrics.totalEmployees} Finalized`}
                                color={summaryMetrics.isFullyFinalized ? "success" : "default"}
                                size="small"
                                sx={{ fontWeight: 700 }}
                            />
                        </Box>
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 4 }}>
                        <Typography variant="caption" color="textSecondary" textTransform="uppercase" fontWeight={700}>Payout Status</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Chip
                                label={summaryMetrics.isFullyPaid ? "Completed" : `${summaryMetrics.paidCount}/${summaryMetrics.totalEmployees} Paid`}
                                color={summaryMetrics.isFullyPaid ? "success" : "info"}
                                size="small"
                                sx={{ fontWeight: 700 }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Filter Controls */}
            <Grid container spacing={2} mb={3}>
                <Grid item size={{ xs: 12, md: 6 }}>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Search employee by ID or name"
                        value={searchQuery}
                        onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                    />
                </Grid>
                <Grid item size={{ xs: 6, md: 4 }}>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Payroll Status</InputLabel>
                        <Select
                            label="Payroll Status"
                            value={statusFilter}
                            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                        >
                            <MenuItem value="All">All Statuses</MenuItem>
                            <MenuItem value="Pending">Pending</MenuItem>
                            <MenuItem value="Locked">Locked</MenuItem>
                            <MenuItem value="Finalized">Finalized</MenuItem>
                            <MenuItem value="Paid">Paid</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item size={{ xs: 6, md: 2 }}>
                    <Button fullWidth startIcon={<ResetIcon />} onClick={handleReset} sx={{ textTransform: 'none', color: 'text.secondary' }}>Reset</Button>
                </Grid>
            </Grid>

            {/* Data Table */}
            <TableContainer component={Paper} sx={{ borderRadius: '12px', border: '1px solid #E2E8F0', boxShadow: 'none' }}>
                <Table sx={{ minWidth: 900 }}>
                    <TableHead sx={{ bgcolor: '#EFF6FF' }}>
                        <TableRow>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Employee ID</TableCell>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Name</TableCell>
                            <TableCell align="center" sx={{ color: '#1D4ED8', fontWeight: 700 }}>Working Days</TableCell>
                            <TableCell align="right" sx={{ color: '#1D4ED8', fontWeight: 700 }}>Gross Salary</TableCell>
                            <TableCell align="right" sx={{ color: '#1D4ED8', fontWeight: 700 }}>Deductions</TableCell>
                            <TableCell align="right" sx={{ color: '#1D4ED8', fontWeight: 700 }}>Leave Encashment</TableCell>
                            <TableCell align="right" sx={{ color: '#1D4ED8', fontWeight: 700 }}>Net Take Home</TableCell>
                            <TableCell align="center" sx={{ color: '#1D4ED8', fontWeight: 700 }}>Status</TableCell>
                            <TableCell align="center" sx={{ color: '#1D4ED8', fontWeight: 700 }}>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody sx={{ bgcolor: '#FFFFFF' }}>
                        {filteredEmployees.length > 0 ? (
                            filteredEmployees.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                                const gross = parseFloat(row.totalGross || 0);
                                const deductions = parseFloat(row.totalDeductions || 0);
                                const encashmentAmount = parseFloat(row.leaveEncashmentAmount || 0);
                                const net = parseFloat(row.netSalary || (gross - deductions + encashmentAmount));

                                let statusLabel = "Pending";
                                let statusColor = "default";
                                if (row.payed_flag) {
                                    statusLabel = "Paid";
                                    statusColor = "success";
                                } else if (row.finalized) {
                                    statusLabel = "Finalized";
                                    statusColor = "primary";
                                } else if (row.locked) {
                                    statusLabel = "Locked";
                                    statusColor = "warning";
                                }

                                return (
                                    <TableRow key={row.id || row.empId} hover sx={{ '&:hover': { bgcolor: '#F1F5F9 !important' } }}>
                                        <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>{row.empId}</TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}>{row.name}</TableCell>
                                        <TableCell align="center">{row.workingDays || row.numOfDays || 30}</TableCell>
                                        <TableCell align="right">₹{gross.toLocaleString()}</TableCell>
                                        <TableCell align="right" sx={{ color: 'error.main' }}>₹{deductions.toLocaleString()}</TableCell>
                                        <TableCell align="right">₹{encashmentAmount.toLocaleString()}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700, color: 'success.main' }}>₹{net.toLocaleString()}</TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={statusLabel}
                                                size="small"
                                                color={statusColor}
                                                variant="outlined"
                                                sx={{ fontWeight: 600 }}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="View Detailed Breakdown">
                                                <IconButton size="small" color="primary" onClick={() => handleViewBreakdown(row)}>
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 10 }}>
                                    <NoDataIcon sx={{ fontSize: 60, color: '#CBD5E1', mb: 1 }} />
                                    <Typography variant="h6" color="textSecondary">No Payroll Records Found</Typography>
                                    <Typography variant="body2" color="textSecondary">Try adjusting search or filter options</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={filteredEmployees.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25]}
            />

            {/* Breakdown Dialog */}
            <Dialog
                open={batchDetailOpen}
                onClose={() => setBatchDetailOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: 4, overflow: "hidden", boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' } }}
            >
                <DialogTitle sx={{ background: 'linear-gradient(45deg, #1a237e 30%, #3949ab 90%)', color: "white", display: "flex", justifyContent: "space-between", alignItems: "center", py: 2.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <SummarizeIcon sx={{ fontSize: 28 }} />
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>Payroll Final Review Breakdown</Typography>
                            <Typography variant="caption" sx={{ opacity: 0.8, textTransform: 'uppercase', letterSpacing: 1 }}>{payPeriod} Cycle</Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={() => setBatchDetailOpen(false)} sx={{ color: "white" }}><CloseIcon /></IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 3, bgcolor: "#f8f9fa" }}>
                    {detailLoading ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 10 }}>
                            <CircularProgress size={50} sx={{ mb: 2 }} />
                            <Typography color="textSecondary">Loading Breakdown Details...</Typography>
                        </Box>
                    ) : selectedBatchDetail ? (
                        <Box>
                            <Card elevation={0} sx={{ borderRadius: 3, my: 3, border: "1px solid #e0e6ed", bgcolor: '#fff' }}>
                                <CardContent sx={{ p: 2 }}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar sx={{ bgcolor: '#e3f2fd', color: '#1a237e', width: 56, height: 56, fontWeight: 'bold' }}>
                                                {(selectedBatchDetail.name || "E").charAt(0)}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="subtitle1" fontWeight={800}>{selectedBatchDetail.name}</Typography>
                                                <Typography variant="body2" color="textSecondary">ID: {selectedBatchDetail.empId}</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item size={{ xs: 12, sm: 6 }} sx={{ textAlign: { sm: 'right' } }}>
                                            <Chip
                                                label={selectedBatchDetail.payed_flag ? "Paid" : selectedBatchDetail.finalized ? "Finalized" : selectedBatchDetail.locked ? "Locked" : "Pending"}
                                                color={selectedBatchDetail.payed_flag ? "success" : selectedBatchDetail.finalized ? "primary" : selectedBatchDetail.locked ? "warning" : "default"}
                                                sx={{ fontWeight: 700, borderRadius: 1.5 }}
                                            />
                                            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                                Working Days: <b>{selectedBatchDetail.workingDays || selectedBatchDetail.numOfDays || 30}</b>
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>

                            <Grid container spacing={3} alignItems="stretch">
                                <Grid item size={{ xs: 12, sm: 6 }} sx={{ display: 'flex' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#2e7d32', display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <PaymentsIcon fontSize="small" /> EARNINGS
                                        </Typography>
                                        <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, bgcolor: '#fff', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                            <Table size="small">
                                                <TableBody>
                                                    <DynamicRow label="Basic Pay" value={selectedBatchDetail.basicPay} />
                                                    <DynamicRow label="HRA" value={selectedBatchDetail.hra} />
                                                    <DynamicRow label="Conveyance" value={selectedBatchDetail.conveyanceAllowance} />
                                                    <DynamicRow label="Special Allowance" value={selectedBatchDetail.specialAllowance} />
                                                    <DynamicRow label="Other Allowances" value={selectedBatchDetail.otherAllowance} />
                                                    <DynamicRow label="Leave Encashment" value={selectedBatchDetail.leaveEncashmentAmount} />
                                                </TableBody>
                                            </Table>
                                            <Box sx={{ mt: 'auto', pt: 2 }}>
                                                <Divider sx={{ mb: 1.5 }} />
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1 }}>
                                                    <Typography variant="subtitle1" fontWeight={800} color="#1a237e">Gross Earnings</Typography>
                                                    <Typography variant="subtitle1" fontWeight={800} color="#1a237e">
                                                        ₹{parseFloat(selectedBatchDetail.totalGross || 0).toLocaleString()}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </Box>
                                </Grid>

                                <Grid item size={{ xs: 12, sm: 6 }} sx={{ display: 'flex' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#d32f2f', display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <AccountBalanceWalletIcon fontSize="small" /> DEDUCTIONS
                                        </Typography>
                                        <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, bgcolor: '#fff', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                            <Table size="small">
                                                <TableBody>
                                                    <DynamicRow label="Provident Fund (PF)" value={selectedBatchDetail.pfEmployee} />
                                                    <DynamicRow label="ESI" value={selectedBatchDetail.esiEmployee} />
                                                    <DynamicRow label="Professional Tax (PT)" value={selectedBatchDetail.professionalTax} />
                                                    <DynamicRow label="Income Tax (TDS)" value={selectedBatchDetail.incomeTax} />
                                                    <DynamicRow label="Education Cess" value={selectedBatchDetail.cess} />
                                                    <DynamicRow label="LOP Deduction" value={selectedBatchDetail.lopAmount} />
                                                </TableBody>
                                            </Table>
                                            <Box sx={{ mt: 'auto', pt: 2 }}>
                                                <Divider sx={{ mb: 1.5 }} />
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1 }}>
                                                    <Typography variant="subtitle1" fontWeight={800} color="#d32f2f">Total Deductions</Typography>
                                                    <Typography variant="subtitle1" fontWeight={800} color="#d32f2f">
                                                        ₹{parseFloat(selectedBatchDetail.totalDeductions || 0).toLocaleString()}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </Box>
                                </Grid>
                            </Grid>

                            <Box sx={{ mt: 4, p: 3, borderRadius: 4, background: 'linear-gradient(90deg, #1a237e 0%, #311b92 100%)', color: 'white', boxShadow: '0 10px 20px -5px rgba(26, 35, 126, 0.4)' }}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item size={{ xs: 12, sm: 5 }}>
                                        <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 1.2, opacity: 0.8 }}>Net Take Home (Final)</Typography>
                                        <Typography variant="h4" fontWeight={800} sx={{ color: '#4caf50' }}>
                                            ₹{parseFloat(selectedBatchDetail.netSalary || 0).toLocaleString()}
                                        </Typography>
                                    </Grid>
                                    <Grid item size={{ md: 1 }} sx={{ display: { xs: 'none', md: 'block' }, textAlign: 'center' }}>
                                        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', height: '60px', mx: 'auto' }} />
                                    </Grid>
                                    <Grid item size={{ xs: 12, sm: 6 }}>
                                        <Stack direction="row" justifyContent="space-between" spacing={3}>
                                            {parseFloat(selectedBatchDetail.lopAmount) > 0 && (
                                                <Box>
                                                    <Typography variant="caption" sx={{ color: '#ff8a80', fontWeight: 700, display: 'block' }}>(-) LOP ({selectedBatchDetail.lopCount} Days)</Typography>
                                                    <Typography variant="h6" fontWeight={700}>₹{parseFloat(selectedBatchDetail.lopAmount).toLocaleString()}</Typography>
                                                </Box>
                                            )}
                                            {parseFloat(selectedBatchDetail.reimbursementAmount) > 0 && (
                                                <Box sx={{ textAlign: 'right', ml: 'auto' }}>
                                                    <Typography variant="caption" sx={{ color: '#b9f6ca', fontWeight: 700, display: 'block' }}>(+) REIMBURSEMENTS</Typography>
                                                    <Typography variant="h6" fontWeight={700}>₹{parseFloat(selectedBatchDetail.reimbursementAmount).toLocaleString()}</Typography>
                                                </Box>
                                            )}
                                            {parseFloat(selectedBatchDetail.lopAmount || 0) === 0 && parseFloat(selectedBatchDetail.reimbursementAmount || 0) === 0 && (
                                                <Typography variant="body2" sx={{ opacity: 0.7, fontStyle: 'italic' }}>No adjustments this cycle.</Typography>
                                            )}
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>
                    ) : null}
                </DialogContent>
            </Dialog>

            {/* Lock & Finalize Confirm Dialog */}
            <Dialog open={finalizeDialogOpen} onClose={() => setFinalizeDialogOpen(false)} PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontWeight: 'bold' }}>
                    <HelpIcon color="primary" /> Finalize & Lock Payroll for {payPeriod}?
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="textSecondary">
                        This action will lock all employee records for <b>{payPeriod}</b> and mark the batch as finalized. Once finalized, individual employee attendance/lop edits will be locked.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ pb: 2, px: 3 }}>
                    <Button onClick={() => setFinalizeDialogOpen(false)} color="inherit" disabled={actionLoading} sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button onClick={handleFinalizeBatch} variant="contained" color="success" disabled={actionLoading} sx={{ textTransform: 'none', borderRadius: 2, px: 3 }}>
                        {actionLoading ? <CircularProgress size={20} /> : "Yes, Finalize & Lock"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Disburse Payout Confirm Dialog */}
            <Dialog open={payoutDialogOpen} onClose={() => setPayoutDialogOpen(false)} PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontWeight: 'bold' }}>
                    <PaymentIcon color="primary" /> Disburse Payroll Payouts for {payPeriod}?
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="textSecondary">
                        Are you sure you want to trigger payout disbursement for all <b>{summaryMetrics.totalEmployees}</b> employees in {payPeriod}?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ pb: 2, px: 3 }}>
                    <Button onClick={() => setPayoutDialogOpen(false)} color="inherit" disabled={actionLoading} sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button onClick={handleDisbursePayout} variant="contained" color="primary" disabled={actionLoading} sx={{ textTransform: 'none', borderRadius: 2, px: 3 }}>
                        {actionLoading ? <CircularProgress size={20} /> : "Confirm Disbursement"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Feedback Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%', borderRadius: 2 }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default ReviewStepper;
